import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LinearProgress } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'material-ui-snackbar-provider';

import Modal from '../../modules/Modal';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { postCreateNewComposer } from '../../../api/composersApi';
import { findSearchValue } from '../../../helpers/getSearchValue';
import { NOTIFICATION, VIDEOPAGE_TYPE } from '../../../enums/ENUMS';
import { PATHS } from '../../../enums/PATHS';
import { parseQueryString } from '../../../helpers/parseQueryString';
import { buildQueryString } from '../../../helpers/buildQueryString';
import { clearVideoFeeds } from '../../../store/videos/videoSlice';
import { getAllWithoutQueryComposers } from '../../../api';
import { VIDEOFEED } from '../../../enums';
import { updateSecondaySelectedClips } from '../../../store/sharedStore/sharedStoreSlice';
import styles from './NewComposerModal.module.scss';
import { useHotkeys } from 'react-hotkeys-hook';
import { getTopicsToDisplay } from '../../../helpers/getTopicsToDisplay';
import { useClipFavourites } from '../../ClipFavourites/context';

type TNewComposerModal = {
  open: boolean;
  switchModal: () => void;
  skipConfirmation?: boolean;
  setSkipConfirmationModals?: (v: boolean) => void;
};

const NewComposerModal: React.FC<TNewComposerModal> = ({ open, switchModal, skipConfirmation, setSkipConfirmationModals }) => {
  const [step, setStep] = useState(1);
  const { currentSelectedComposerClips, composerRows, composerPlayControl } = useAppSelector((state) => state.composersStore);
  const { secondarySelectedClips, preserveSecondaryClipOrder } = useAppSelector((state) => state.sharedStore);
  const toSecondStepButtonRef = useRef<HTMLDivElement>(null);
  const toThirdStepButtonRef = useRef<HTMLDivElement>(null);
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const dispatch = useAppDispatch();
  const notification = useSnackbar();
  const { reload: reloadFavourites } = useClipFavourites();
  const [composerId, setComposerId] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;

    if (step === 2) {
      const rafId = requestAnimationFrame(() => {
        inputRef.current?.focus();
      });

      return () => cancelAnimationFrame(rafId);
    }
  }, [open, step]);

  useEffect(() => {
    if (skipConfirmation && open) {
      setStep(2);
    }
  }, [skipConfirmation, open]);

  const closeModal = () => {
    switchModal();
    setLoading(false);
    setStep(1);
    setComposerId('');
    setTitle('');
    dispatch(updateSecondaySelectedClips([]));
    if (setSkipConfirmationModals) {
      setSkipConfirmationModals(false);
    }
  };

  const nextStep = () => {
    setStep(2);
  };

  const createWithAllSelected = () => {
    dispatch(updateSecondaySelectedClips([]));
    nextStep();
  };

  const createWithCurrentClip = () => {
    const rowIndex = composerPlayControl.currentRowIndex;
    const clipIndex = composerPlayControl.currentClipIndex;
    const row = composerRows?.[rowIndex];
    const clipFromRow = row?.clips?.[clipIndex];
    let currentClip: any = clipFromRow
      ? {
          ...clipFromRow,
          rowIndex,
          clipIndex,
          topicId: row.id,
          topicName: row.name,
        }
      : currentSelectedComposerClips.find((c) => c.rowIndex === rowIndex && c.clipIndex === clipIndex);

    if (!currentClip) return;

    const resolvedRowIndex = typeof currentClip.rowIndex === 'number' ? currentClip.rowIndex : rowIndex;
    const resolvedRow = composerRows?.[resolvedRowIndex];
    const normalizedCurrentClip = {
      ...currentClip,
      rowIndex: resolvedRowIndex,
      clipIndex: typeof currentClip.clipIndex === 'number' ? currentClip.clipIndex : clipIndex,
      topicId: currentClip.topicId || resolvedRow?.id,
      topicName: currentClip.topicName || resolvedRow?.name,
    } as any;

    if (!normalizedCurrentClip.topicId) return;
    dispatch(updateSecondaySelectedClips([normalizedCurrentClip]));
    nextStep();
  };

  const onSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    e && e.preventDefault();
    const feedId = findSearchValue('feedId') as string;
    try {
      setLoading(true);
      const body = {
        title,
        videofeedId: feedId,
        topicsIds: currentSelectedComposerClips
          .map((topic) => composerRows[topic.rowIndex!].id)
          .filter((value, index, array) => array.indexOf(value) === index),
        clips: currentSelectedComposerClips.map((clip) => ({ id: clip.id, time_start: clip.time_start, time_end: clip.time_end })),
      };
      if (secondarySelectedClips.length) {
        const normalizedSecondaryClips = secondarySelectedClips
          .map((clip: any) => {
            const row = typeof clip?.rowIndex === 'number' ? composerRows[clip.rowIndex] : undefined;
            return {
              ...clip,
              topicId: clip?.topicId || row?.id,
              topicName: clip?.topicName || row?.name,
            };
          })
          .filter((clip: any) => !!clip?.topicId && !!clip?.id);

        const topicClips = preserveSecondaryClipOrder
          ? normalizedSecondaryClips.slice().sort((a, b) => a.rowIndex! - b.rowIndex!) : normalizedSecondaryClips;
        body.topicsIds = topicClips
          .map((clip: any) => clip.topicId!)
          .filter((value: string, index: number, array: string[]) => array.indexOf(value) === index);
        body.clips = normalizedSecondaryClips.map((clip: any) => ({ id: clip.id, time_start: clip.time_start, time_end: clip.time_end }));
      }
      const res = await postCreateNewComposer(body);
      reloadFavourites();
      setComposerId(res?.data?.id);
      setStep(3);
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage(NOTIFICATION.SOMETHING_WENT_WRONG);
      }
    } finally {
      setLoading(false);
    }
  };

  const onProceed = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    try {
      const paramsObject = parseQueryString(window.location.search);
      paramsObject.feedId = composerId;
      delete paramsObject.folderId;
      delete paramsObject.parentPage;
      delete paramsObject.pageType;
      const allUserComposers = await getAllWithoutQueryComposers();
      paramsObject.feed = allUserComposers.data.documents.findIndex((composer: VIDEOFEED) => composer.id === paramsObject.feedId);
      history.push(`${PATHS.COMPOSER_PAGE.path}?${buildQueryString(paramsObject)}&pageType=${VIDEOPAGE_TYPE.MY_COMPOSER}`);
      dispatch(clearVideoFeeds());
      closeModal();
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const onTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const topicsToDisplay = useMemo(
    () =>
      getTopicsToDisplay({
        currentSelectedComposerClips,
        secondarySelectedClips,
        composerRows,
      }),
    [currentSelectedComposerClips, secondarySelectedClips, composerRows],
  );
  const showMultiSelectionConfirm = secondarySelectedClips.length === 0 && currentSelectedComposerClips.length > 1;

  useHotkeys('Enter', () => {
    if (!open || loading) return;

    switch (step) {
      case 1:
        toSecondStepButtonRef?.current?.click();
        break;
      case 2:
        toThirdStepButtonRef?.current?.click();
        break;
      case 3:
        submitButtonRef?.current?.click();
        break;
      default:
        break;
    }
  });

  return (
    <Modal open={open} onClose={closeModal} loading={loading}>
      {step === 1 && (
        <div className={styles.modal}>
          {showMultiSelectionConfirm ? (
            <>
              <div className={styles.modalTitle}>Do you want to create a new composer?</div>
              <div className={styles.actionBtns}>
                <ContainedButton onClick={createWithAllSelected}>
                  <div ref={toSecondStepButtonRef}>Create with all {currentSelectedComposerClips.length} selected clip(s)</div>
                </ContainedButton>
                <ContainedButton onClick={createWithCurrentClip}>Create with current playing clip</ContainedButton>
                <OutlinedButton onClick={closeModal}>Cancel</OutlinedButton>
              </div>
            </>
          ) : (
            <>
              <div className={styles.modalTitle}>Would you like to create a new composer?</div>
              {Object.keys(topicsToDisplay).length > 0 && (
                <div className={styles.clipSummary}>
                  {Object.keys(topicsToDisplay).map((key) => (
                    <div key={key} className={styles.topicRow}>
                      <div className={styles.topicName}>{topicsToDisplay[key].topicName}</div>
                      <div className={styles.clipCount}>
                        {topicsToDisplay[key].clipCount} {topicsToDisplay[key].clipCount === 1 ? 'clip' : 'clips'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className={styles.actionBtns}>
                <ContainedButton onClick={nextStep}>
                  <div ref={toSecondStepButtonRef}>Create</div>
                </ContainedButton>
                <OutlinedButton onClick={closeModal}>Cancel</OutlinedButton>
              </div>
            </>
          )}
        </div>
      )}

      {step === 2 && (
        <form className={styles.modal} onSubmit={onSubmit}>
          <div className={styles.modalTitle}>Please name the composer</div>
          <div>
            <input
              name="title"
              onChange={onTitleChange}
              ref={inputRef}
              required
              value={title}
              className={styles.titleField}
              placeholder={'Enter Composer Name'}
            />
          </div>
          <div className={styles.actionBtns}>
            <ContainedButton type="submit">
              <div ref={toThirdStepButtonRef}>Create</div>
            </ContainedButton>
            <OutlinedButton onClick={closeModal}>Cancel</OutlinedButton>
          </div>
        </form>
      )}

      {step === 3 && (
        <form className={styles.modal} onSubmit={onProceed}>
          <div className={styles.modalTitle}>Composer is created</div>
          <div className={styles.actionBtns}>
            <ContainedButton type="submit">
              <div ref={submitButtonRef}>Proceed to composer</div>
            </ContainedButton>
            <OutlinedButton onClick={closeModal}>Stay here</OutlinedButton>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default NewComposerModal;
